import {
  type JSX,
  createSignal,
  createEffect,
  createResource,
  onCleanup,
  Show,
} from 'solid-js';
import { useApiServices } from '@trimble/tc-issue-management-providers';
import { buildAuthImg } from '../../utils/util';

const MAX_CACHE_ITEMS = 50;

class ThumbnailCache {
  private readonly cache = new Map<string, { time: number; dataUrl: string }>();

  public has(key: string): boolean {
    return this.cache.has(key);
  }

  public get(key: string): string {
    return this.cache.get(key)?.dataUrl ?? '';
  }

  public set(key: string, dataUrl: string): void {
    if (!this.cache.has(key)) {
      while (this.cache.size >= MAX_CACHE_ITEMS) {
        const oldest = this.oldestKey();
        if (!oldest) break;
        this.cache.delete(oldest);
      }
    }
    this.cache.set(key, { time: Date.now(), dataUrl });
  }

  private oldestKey(): string | undefined {
    let oldestTime = Infinity;
    let oldestKey: string | undefined;
    for (const [key, value] of this.cache) {
      if (value.time < oldestTime) {
        oldestTime = value.time;
        oldestKey = key;
      }
    }
    return oldestKey;
  }
}

const issueThumbnailCache = new ThumbnailCache();

export interface IssueThumbnailProps {
  issueId: string;
  projectId: string;
  contextId: string;
  accessToken?: string;
}

/**
 * Lazy-loading thumbnail for an issue card. Uses IntersectionObserver to
 * enable the TCPS views query only when the element scrolls into view,
 * then fetches the first viewpoint thumbnail and caches it.
 */
export function IssueThumbnail(props: IssueThumbnailProps): JSX.Element {
  const apiServices = useApiServices();

  const [isVisible, setIsVisible] = createSignal(false);

  // Skip IntersectionObserver for cached thumbnails
  createEffect(() => {
    if (issueThumbnailCache.has(props.issueId)) {
      setIsVisible(true);
    }
  });

  let containerRef: HTMLDivElement | undefined;

  // IntersectionObserver to flip isVisible when the card enters the viewport
  createEffect(() => {
    if (issueThumbnailCache.has(props.issueId) || isVisible()) return;
    if (!containerRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef);

    onCleanup(() => observer.disconnect());
  });

  // TanStack Query — only fires when isVisible becomes true
  // eslint-disable-next-line solid/reactivity -- TanStack Solid Query tracks the getter function internally
  const viewsQuery = apiServices.viewApi.listAllViewsInProjectQuery(() => {
    if (!isVisible() || issueThumbnailCache.has(props.issueId)) {
      return undefined;
    }
    return {
      query: {
        projectId: props.projectId,
        issueId: props.issueId,
        contextId: props.contextId,
        detail: false,
      },
    };
  });

  // Fetch thumbnail as blob via createResource (replaces manual async .then() chain)
  const [thumbnailData] = createResource(
    () => {
      if (!isVisible()) return false as const;
      const issueId = props.issueId;
      if (issueThumbnailCache.has(issueId)) return false as const;
      if (!viewsQuery.isSuccess) return false as const;

      const views = viewsQuery.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ViewDetailsResponse thumbnail field access
      const thumbnailUrl = (views as any)?.[0]?.thumbnail as string | undefined;
      if (!thumbnailUrl) {
        issueThumbnailCache.set(issueId, '');
        return false as const;
      }

      return { url: thumbnailUrl, accessToken: props.accessToken, issueId };
    },
    async (source) => {
      try {
        const result = await buildAuthImg(source.url, source.accessToken);
        issueThumbnailCache.set(source.issueId, result);
        return result;
      } catch (error) {
        console.error('Error fetching issue thumbnail:', error);
        issueThumbnailCache.set(source.issueId, '');
        return '';
      }
    }
  );

  const dataUrl = (): string => {
    if (issueThumbnailCache.has(props.issueId)) {
      return issueThumbnailCache.get(props.issueId);
    }
    return thumbnailData() ?? '';
  };

  const isLoading = (): boolean => {
    if (issueThumbnailCache.has(props.issueId)) return false;
    if (!isVisible()) return true;
    if (!viewsQuery.isSuccess) return true;
    return thumbnailData.loading;
  };

  const shouldHide = (): boolean => !isLoading() && !dataUrl();

  return (
    <Show when={!shouldHide()}>
      <div
        ref={containerRef}
        class="flex-shrink-0 w-[40px] h-[40px] overflow-hidden rounded-sm"
      >
        <Show
          when={!isLoading() && dataUrl()}
          fallback={
            <div class="animate-pulse w-[40px] h-[40px] bg-[#EAEAEF]" />
          }
        >
          <img
            src={dataUrl()}
            alt=""
            class="w-[40px] h-[40px] block object-cover"
          />
        </Show>
      </div>
    </Show>
  );
}
